import {
	AlertManager,
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	CommunityPostPagingInformationModel,
	CommunityPostPreviewModel,
	list
} from "client/Api/Communities/communityFeed";
import {
	Dispatch,
	ReadonlyRefObject,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { CommunityId } from "./CommunityFeedPage";
import { CommentWithRepliesModel } from "../HubCommentSection/HubCommentSection";
import { getStructuredComments } from "../common";
import {
	HubCommentModel,
	HubCommentStatisticsModel,
	HubCommentPagingInformationModel
} from "client/Api/Hub/common";
import { SortDirection } from "client/Api/common";
import {
	CommunityPostStatisticsModel,
	getStatistics,
	getCommentsList
} from "client/Api/Communities/communityPostViewer";
import { getPagingInformationModelFromComment } from "../HubCommentSection/hooks/useCommentSection";

const COMMENT_PAGE_SIZE = 10;

export type CommunityPostListLoadingStatus = "loading" | "loaded" | "error";

export type PostListState = {
	status: CommunityPostListLoadingStatus;
	hasMore: boolean;
	items: readonly PostListItem[];
};

type PostListItemComments = {
	status: "loading" | "loaded" | "error";
	items: readonly CommentWithRepliesModel[];
	hasMore: boolean;
	loadMore: () => void;
	onDeleteComment: (commentId: number) => void;
	onUpdateComment: (comment: CommentWithRepliesModel) => void;
	onNewComment: (comment: CommentWithRepliesModel) => void;
	onUpdateCommentStatistics: (
		commentId: number,
		statistics: HubCommentStatisticsModel
	) => void;
};

export type PostListItem = Omit<CommunityPostPreviewModel, "comments"> & {
	comments: PostListItemComments;
};

export type PostListData = {
	loadMore: () => void;
	reload: () => void;
	state: PostListState;
	setState: Dispatch<SetStateAction<PostListState>>;
	updatePost: (post: PostListItem) => void;
	addNewPost: (post: CommunityPostPreviewModel) => void;
	deletePost: (postId: number) => void;
};

export const useCommunityPostListDataSource = (
	communityId: CommunityId,
	pageSize: number,
	alertManager: AlertManager
): PostListData => {
	const [state, setState] = useState<PostListState>(initialState);
	const requestCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const commentsCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const postStatisticsCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);

	const paginationInfo = useMemo(
		(): CommunityPostPagingInformationModel | null => {
			const { items } = state;

			return items.length > 0
				? getPagingInformationModelFromPost(items[items.length - 1])
				: null;
		},
		[state]
	);

	const stableState: ReadonlyRefObject<PostListState> = useSynchronizedRef(state);

	const loadMoreCommentsHandler = useCallback(
		async (postId: number): Promise<void> => {
			const post: PostListItem | undefined = stableState.current
				.items
				.find(x => x.id === postId);

			if (isNil(post)) {
				throw new Error(`did not find post with id: ${postId}`);
			}

			const {
				items,
				hasMore,
				status
			} = post.comments;

			if (!hasMore || status === "loading") {
				return;
			}

			commentsCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			commentsCancellationRef.current = cancellation;

			const after: HubCommentPagingInformationModel | null = items.length > 0
				? getPagingInformationModelFromComment(items[items.length - 1])
				: null;

			setState(
				(x): PostListState => updatePostComments(
					x,
					postId,
					(comments: PostListItemComments): PostListItemComments => ({
						...comments,
						status: "loading"
					})
				)
			);

			try {
				const pageResult: LazyLoadPageResult<HubCommentModel> = await getCommentsList(
					postId,
					COMMENT_PAGE_SIZE,
					after,
					SortDirection.Descending,
					cancellation.signal
				);

				const structuredComments: readonly CommentWithRepliesModel[] = getStructuredComments(
					pageResult.items
				);

				setState(
					(x): PostListState => updatePostComments(
						x,
						postId,
						(comments: PostListItemComments): PostListItemComments => ({
							...comments,
							status: "loaded",
							hasMore: pageResult.hasMore,
							items: [
								...comments.items,
								...structuredComments
							]
						})
					)
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setState(
					(x): PostListState => updatePostComments(
						x,
						postId,
						(comments: PostListItemComments): PostListItemComments => ({
							...comments,
							status: "error",
							hasMore: comments.hasMore
						})
					)
				);

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[alertManager, stableState]
	);

	const updateCommentHandler = useCallback(
		(postId: number, comment: HubCommentModel): void => {
			setState(
				(x): PostListState => updatePostComments(
					x,
					postId,
					(comments: PostListItemComments): PostListItemComments => (
						{
							...comments,
							items: updateCommentById(
								comments.items,
								comment.id,
								(c: CommentWithRepliesModel): CommentWithRepliesModel => ({
									...comment,
									replies: c.replies
								}),
							)
						}
					)
				)
			);
		},
		[]
	);

	const refreshPostStatisticsHandler = useCallback(
		async (postId: number): Promise<void> => {
			try {
				postStatisticsCancellationRef.current
					?.abort();

				const cancellation = new AbortController();
				postStatisticsCancellationRef.current = cancellation;

				const statistics: CommunityPostStatisticsModel = await getStatistics(
					postId,
					cancellation.signal
				);

				setState(
					(x): PostListState => ({
						...x,
						items: x.items
							.map((post: PostListItem): PostListItem => {
								if (post.id !== postId) {
									return post;
								}

								return {
									...post,
									statistics
								};
							})
					})
				);

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
		]
	);

	const deleteCommentHandler = useCallback(
		(postId: number, commentId: number): void => {
			setState(
				(x): PostListState => updatePostComments(
					x,
					postId,
					(comments: PostListItemComments): PostListItemComments => ({
						...comments,
						items: removeCommentById(comments.items, commentId)
					})
				)
			);

			refreshPostStatisticsHandler(postId);
		},
		[refreshPostStatisticsHandler]
	);

	const updateCommentStatisticsHandler = useCallback(
		(
			postId: number,
			commentId: number,
			statistics: HubCommentStatisticsModel
		): void => {
			setState(
				(x): PostListState => updatePostComments(
					x,
					postId,
					(comments: PostListItemComments): PostListItemComments => ({
						...comments,
						items: updateCommentById(
							comments.items,
							commentId,
							(c): HubCommentModel => ({...c, statistics}),
						)
					})
				)
			);
		},
		[]
	);

	const addNewCommentHandler = useCallback(
		(postId: number, comment: HubCommentModel): void => {
			setState(
				(x): PostListState => updatePostComments(
					x,
					postId,
					(comments: PostListItemComments): PostListItemComments => ({
						...comments,
						items: isNil(comment.parentCommentId)
							? [
								comment,
								...comments.items
							]
							: updateCommentById(
								comments.items,
								comment.parentCommentId,
								(x: CommentWithRepliesModel): CommentWithRepliesModel => ({
									...x,
									replies: isNil(x.replies)
										? [comment]
										: [
											...x.replies,
											comment
										]

								})
							)

					})
				)
			);

			refreshPostStatisticsHandler(postId);
		},
		[refreshPostStatisticsHandler]
	);

	const convertPostPreviewModelToPostListItem = useCallback(
		(post: CommunityPostPreviewModel): PostListItem => {
			const { comments } = post;

			const structuredComments: readonly CommentWithRepliesModel[] = getStructuredComments(
				comments.items
			);

			return {
				...post,
				comments: {
					status: "loaded",
					items: structuredComments,
					hasMore: comments.hasMore,
					loadMore: () => loadMoreCommentsHandler(post.id),
					onDeleteComment: (commentId: number): void => {
						deleteCommentHandler(post.id, commentId);
					},
					onUpdateComment: (comment: CommentWithRepliesModel): void => {
						updateCommentHandler(post.id, comment);
					},
					onNewComment: (comment: CommentWithRepliesModel): void => {
						addNewCommentHandler(post.id, comment);
					},
					onUpdateCommentStatistics: (
						commentId: number,
						statistics: HubCommentStatisticsModel
					): void => {
						updateCommentStatisticsHandler(
							post.id,
							commentId,
							statistics
						);
					},
				}
			};
		},
		[
			loadMoreCommentsHandler,
			deleteCommentHandler,
			updateCommentHandler,
			addNewCommentHandler,
			updateCommentStatisticsHandler
		]
	);

	const fetchItems = useCallback(
		async (afterPost: CommunityPostPagingInformationModel | null = null): Promise<void> => {
			requestCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			requestCancellationRef.current = cancellation;

			setState(
				(x: PostListState): PostListState => ({
					...x,
					status: "loading"
				})
			);

			try {

				const pageResult: LazyLoadPageResult<CommunityPostPreviewModel> = await list(
					communityId,
					afterPost,
					pageSize,
					cancellation.signal
				);

				setState(
					(x): PostListState => ({
						hasMore: pageResult.hasMore,
						status: "loaded",
						items: [
							...x.items,
							...pageResult.items
								.map((x: CommunityPostPreviewModel): PostListItem => {
									return convertPostPreviewModelToPostListItem(x);
								})
						]
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setState(
					(x): PostListState => ({
						hasMore: false,
						status: "error",
						items: x.items
					})
				);
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			communityId,
			alertManager,
			pageSize,
			setState,
			convertPostPreviewModelToPostListItem
		]
	);

	const stableFetchItems = useStableImperativeCallback(fetchItems);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(communityId);

			return (): void => {
				postStatisticsCancellationRef.current
					?.abort();

				commentsCancellationRef.current
					?.abort();
			};
		},
		[communityId]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(communityId);

			setState(initialState);

			stableFetchItems();

			return (): void => {
				requestCancellationRef.current
					?.abort();
			};
		},
		[stableFetchItems, communityId]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			if (!state.hasMore || state.status === "loading") {
				return;
			}

			fetchItems(paginationInfo);
		},
		[
			fetchItems,
			state.hasMore,
			state.status,
			paginationInfo
		]
	);

	const reloadHandler = useCallback(
		(): void => {
			setState(initialState);
			fetchItems();
		},
		[
			fetchItems,
		]
	);

	const updatePostHandler = useCallback(
		(post: PostListItem): void => {
			setState(
				(x): PostListState => ({
					...x,
					items: x.items
						.map((x: PostListItem): PostListItem => {
							if (x.id === post.id) {
								return post;
							}

							return x;
						})
				})
			);
		},
		[setState]
	);

	const addPostHandler = useCallback(
		(post: CommunityPostPreviewModel): void => {
			setState(
				(x: PostListState): PostListState => ({
					hasMore: x.hasMore,
					status: x.status,
					items: [
						convertPostPreviewModelToPostListItem(post),
						...x.items
					]
				})
			);
		},
		[
			setState,
			convertPostPreviewModelToPostListItem
		]
	);

	const deletePostHandler = useCallback(
		(postId: number): void => {
			setState(
				(x): PostListState => ({
					...x,
					items: x.items
						.filter(
							(x: PostListItem): boolean => x.id !== postId
						)
				})
			);
		},
		[setState]
	);

	return useMemo(
		(): PostListData => {
			return {
				loadMore: loadMoreHandler,
				reload: reloadHandler,
				deletePost: deletePostHandler,
				updatePost: updatePostHandler,
				addNewPost: addPostHandler,
				setState,
				state
			};
		},
		[
			loadMoreHandler,
			reloadHandler,
			deletePostHandler,
			updatePostHandler,
			addPostHandler,
			state
		]
	);
};

const initialState: PostListState = {
	hasMore: false,
	items: [],
	status: "loading"
};

function getPagingInformationModelFromPost(
	post: PostListItem
): CommunityPostPagingInformationModel {
	return {
		id: post.id,
		createdOn: post.createdOn
	};
}

function updatePostComments(
	state: PostListState,
	postId: number,
	updateCallback: (comments: PostListItemComments) => PostListItemComments
): PostListState {
	return {
		...state,
		items: state.items
			.map((post: PostListItem): PostListItem => {
				if (post.id !== postId) {
					return post;
				}

				return {
					...post,
					comments: updateCallback(post.comments)
				};
			})
	};
}

function updateCommentById(
	commentsList: readonly CommentWithRepliesModel[],
	commentId: number,
	updateCallback: {
		(comment: CommentWithRepliesModel): CommentWithRepliesModel;
		(comment: HubCommentModel): HubCommentModel;
	}
): readonly CommentWithRepliesModel[] {
	const result: CommentWithRepliesModel[] = [];

	for (const comment of commentsList) {
		if (comment.id === commentId) {
			result.push(updateCallback(comment));
			continue;
		}

		if (comment.replies) {
			const replyIndex: number = comment.replies.findIndex(
				(reply) => reply.id === commentId
			);

			if (replyIndex !== -1) {
				const updatedReplies: HubCommentModel[] = [...comment.replies];
				updatedReplies[replyIndex] = updateCallback(
					updatedReplies[replyIndex]
				);

				result.push({
					...comment,
					replies: updatedReplies,
				});
			} else {
				result.push(comment);
			}
		} else {
			result.push(comment);
		}
	}

	return result;
}

function removeCommentById(
	comments: readonly CommentWithRepliesModel[],
	id: number
): readonly CommentWithRepliesModel[] {
	const result: CommentWithRepliesModel[] = [];

	for (const comment of comments) {
		if (comment.id === id) {
			continue;
		}

		if (isNotNil(comment.replies)) {
			const replyIndex: number = comment.replies
				.findIndex(x => x.id === id);

			if (replyIndex === -1) {
				result.push(comment);
			} else {
				const filteredReplies: HubCommentModel[] = [...comment.replies];
				filteredReplies.splice(replyIndex, 1);

				result.push({
					...comment,
					replies: filteredReplies
				});
			}
		} else {
			result.push(comment);
		}
	}

	return result;
}